import {
    invoiceAmount,
    invoiceCombined,
    vat18FromCombined,
} from './orderInvoiceMath';
import { normalizeCartLinesForPdf } from './normalizeCartLinesForPdf';

/** ISO / Mongo-style timestamps → readable PDF date (Malta/EU locale). */
export function formatPdfDisplayDate(value) {
    if (value == null || value === '') return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Plain view-model for PDF templates. Callers pass API `order` objects;
 * templates only depend on this shape.
 *
 * @param {Record<string, unknown>} order
 * @param {{
 *   old?: boolean;
 *   variant?: 'order' | 'delivery' | 'missing';
 * }} [options]
 */
export function normalizeOrderForPdf(order, options = {}) {
    const old = Boolean(options.old);
    const variant = options.variant ?? 'order';

    const combined = invoiceCombined(order);
    const vat = vat18FromCombined(combined);

    const billingName = old ? order?.owner?.name : order?.name;
    const billingCompany = old ? order?.owner?.company : order?.company;
    const billingVat = old ? order?.owner?.VAT : order?.VAT;
    const billingStreet = old ? order?.owner?.street : order?.street;
    const billingCity = old ? order?.owner?.city : order?.city;
    const billingPost = old ? order?.owner?.postCode : order?.postCode;
    const billingPhone = old ? order?.owner?.phone : order?.phone;
    const billingEmail = old ? order?.owner?.email : order?.email;

    const delivery = {
        name: order?.deliveryname || order?.deliveryName || '',
        company: order?.deliverycompanyName || order?.deliveryCompanyName || '',
        vat: order?.deliveryVAT || '',
        street: order?.deliverystreet || order?.deliveryStreet || '',
        city: order?.deliverycity || order?.deliveryCity || '',
        postCode: order?.deliverypostCode || order?.deliveryPostCode || '',
        phone: order?.deliveryphone || order?.deliveryPhone || '',
        email: order?.deliveryemail || order?.deliveryEmail || '',
    };

    const orderNumber =
        order?.orderNumber != null ? String(order.orderNumber) : String(order?.id ?? '');
    const invoiceNumber =
        order?.invoice?.invoiceNumber != null
            ? String(order.invoice.invoiceNumber)
            : null;

    const invoiceStatus =
        order?.invoice?.status != null ? String(order.invoice.status) : '';
    const invoiceDate =
        order?.invoice?.date != null ? formatPdfDisplayDate(order.invoice.date) : '';

    return {
        variant,
        title:
            variant === 'delivery'
                ? 'Delivery note'
                : variant === 'missing'
                    ? 'Missing note'
                    : 'Order summary',
        orderNumber,
        invoiceNumber,
        invoiceStatus,
        invoiceDate,
        status: order?.status != null ? String(order.status) : '',
        orderDate: order?.date != null ? formatPdfDisplayDate(order.date) : '',
        note: order?.note != null ? String(order.note) : '',

        cartLines: normalizeCartLinesForPdf(order?.cart),

        invoice: {
            orderSubtotal: invoiceAmount(order?.total),
            surcharge: invoiceAmount(order?.surcharge),
            shipping: invoiceAmount(order?.shipping),
            vat18: vat,
            combined,
            method:
                order?.method && order.method !== 'vivaWallet'
                    ? String(order.method)
                    : '',
        },

        billing: {
            name: billingName != null ? String(billingName) : '',
            company: billingCompany != null ? String(billingCompany) : '',
            vat: billingVat != null ? String(billingVat) : '',
            street: billingStreet != null ? String(billingStreet) : '',
            city: billingCity != null ? String(billingCity) : '',
            postCode: billingPost != null ? String(billingPost) : '',
            phone: billingPhone != null ? String(billingPhone) : '',
            email: billingEmail != null ? String(billingEmail) : '',
        },

        delivery,
    };
}
